import React, { useEffect, useRef } from "react";
import {
  FaHashtag,
  FaLayerGroup,
  FaTimes,
  FaToggleOn,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";

import {
  DEFAULT_AVATAR,
  getAdherentName,
  getAdherentPhoto,
  getAllCoachNames,
  getGroupAdherents,
  getGroupAdherentsCount,
  getGroupColorClasses,
  getGroupName,
  getGroupUsers,
  getStatusLabel,
  getStatusStyle,
  getUserName,
  getUserPhoto,
} from "../utils/adminGroupsUtils";

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-slate-950 dark:text-sky-200">
            <Icon />
          </div>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>

          <p className="mt-2 break-words text-sm font-bold text-slate-800 dark:text-white/80">
            {value || "Non renseigné"}
          </p>
        </div>
      </div>
    </div>
  );
}

function GroupDetailsDrawer({ isOpen, group, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !group) return null;

  const color = getGroupColorClasses(group);
  const adherents = getGroupAdherents(group);
  const users = getGroupUsers(group);
  const count = getGroupAdherentsCount(group);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <aside
        className="h-full w-full max-w-3xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className={`bg-gradient-to-br ${color.gradient} p-5 text-white`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.5rem] bg-white/15 text-2xl backdrop-blur-xl">
                  <FaLayerGroup />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                    Fiche groupe
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold">
                    {getGroupName(group)}
                  </h2>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex min-h-8 items-center gap-2 rounded-2xl bg-white/15 px-3 text-xs font-bold text-white">
                      <FaUsers />
                      {count} adhérent{count > 1 ? "s" : ""}
                    </span>

                    <span
                      className={`inline-flex min-h-8 items-center rounded-2xl px-3 text-xs font-bold ${getStatusStyle(
                        group,
                      )}`}
                    >
                      {getStatusLabel(group)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoBox icon={FaHashtag} label="ID groupe" value={`#${group.id}`} />
              <InfoBox icon={FaToggleOn} label="Statut" value={getStatusLabel(group)} />
              <InfoBox icon={FaHashtag} label="Ordre" value={group.ordre_groupe ?? 0} />
              <InfoBox icon={FaUsers} label="Quota" value={group.quota_groupe ?? "Non défini"} />
              <InfoBox icon={FaUserTie} label="Coach principal" value={getAllCoachNames(group)} />
              <InfoBox icon={FaLayerGroup} label="Couleur" value={group.color_groupe || "Non renseignée"} />
            </div>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                  <FaUserTie />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Utilisateurs rattachés
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {users.length} utilisateur{users.length > 1 ? "s" : ""}
                  </h3>
                </div>
              </div>

              {users.length > 0 ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950"
                    >
                      <img
                        src={getUserPhoto(user)}
                        alt={getUserName(user)}
                        className="h-11 w-11 rounded-2xl object-cover"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }}
                      />

                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                          {getUserName(user)}
                        </p>

                        <p className="truncate text-xs font-semibold text-slate-400">
                          {user.email || `ID #${user.id}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-bold text-slate-400 dark:border-white/10 dark:bg-slate-950">
                  Aucun utilisateur rattaché.
                </div>
              )}
            </section>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${color.badge}`}>
                  <FaUsers />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Liste des adhérents
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {adherents.length} judoka{adherents.length > 1 ? "s" : ""}
                  </h3>
                </div>
              </div>

              {adherents.length > 0 ? (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {adherents.map((adherent) => (
                    <div
                      key={adherent.id}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950"
                    >
                      <img
                        src={getAdherentPhoto(adherent)}
                        alt={getAdherentName(adherent)}
                        className="h-11 w-11 rounded-2xl object-cover"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }}
                      />

                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                          {getAdherentName(adherent)}
                        </p>

                        <p className="truncate text-xs font-semibold text-slate-400">
                          ID #{adherent.id} · {adherent.club || "Club non renseigné"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-bold text-slate-400 dark:border-white/10 dark:bg-slate-950">
                  Aucun adhérent dans ce groupe.
                </div>
              )}
            </section>
          </div>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
            >
              <FaTimes />
              Fermer la fiche groupe
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default GroupDetailsDrawer;